import { cloneHeadersWithAuth } from '@app/utils/api';
import {
  CredentialKindschema,
  ResponseError,
  type Credential,
  type CredentialDetailsApiRestAuthMtls,
  type CredentialDetailsApiRestschema,
  type CredentialDetailsCliSshschema,
  type CredentialDetailsGnmischema,
  type CredentialDetailsSnmpV2cschema,
  type CredentialDetailsSnmpV3schema,
  type SecretsApiClass
} from '@app/types/sdk';

interface DeleteSecretOptions {
  secretsApi: SecretsApiClass;
  orgId: string;
  secretId: string;
  etag?: string | null;
  suppressNotFound?: boolean;
}

const CERTIFICATE_PEM_BEGIN = '-----BEGIN CERTIFICATE-----';
const CERTIFICATE_PEM_END = '-----END CERTIFICATE-----';

export const containsCertificatePemBlock = (value: string): boolean => {
  const beginIndex = value.indexOf(CERTIFICATE_PEM_BEGIN);
  if (beginIndex < 0) {
    return false;
  }

  const contentsStart = beginIndex + CERTIFICATE_PEM_BEGIN.length;
  const endIndex = value.indexOf(CERTIFICATE_PEM_END, contentsStart);
  return endIndex >= 0 && value.slice(contentsStart, endIndex).trim().length > 0;
};

export const deleteSecretWithAuth = async ({
  secretsApi,
  orgId,
  secretId,
  etag,
  suppressNotFound = true
}: DeleteSecretOptions): Promise<void> => {
  let resolvedEtag = etag ?? undefined;

  if (!resolvedEtag) {
    try {
      const metadataResponse = await secretsApi.orgsOrgIdSecretsSecretIdGetRaw(
        { orgId, secretId },
        async ({ init }) => ({
          headers: await cloneHeadersWithAuth(init.headers ?? undefined)
        })
      );
      resolvedEtag = metadataResponse.raw.headers.get('ETag') ?? undefined;
    } catch (error) {
      if (suppressNotFound && error instanceof ResponseError && error.response.status === 404) {
        return;
      }
      throw error;
    }
  }

  const deleteResponse = await secretsApi.orgsOrgIdSecretsSecretIdDeleteRaw(
    { orgId, secretId },
    async ({ init }) => {
      const headers = await cloneHeadersWithAuth(init.headers ?? undefined);
      if (resolvedEtag) {
        headers['If-Match'] = resolvedEtag;
      }
      return { headers };
    }
  );

  await deleteResponse.value();
};

export const collectCredentialSecretIds = (credential: Credential | null | undefined): string[] => {
  if (!credential || !credential.details) {
    return [];
  }

  switch (credential.kind) {
    case CredentialKindschema.CliSsh: {
      const details = credential.details as CredentialDetailsCliSshschema;
      return [
        details.execPasswordSecretId,
        details.enablePasswordSecretId,
        details.sshPrivateKeySecretId,
        details.sshPassphraseSecretId
      ].filter((value): value is string => Boolean(value));
    }
    case CredentialKindschema.Gnmi: {
      const details = credential.details as CredentialDetailsGnmischema;
      return [details.passwordSecretId, details.deviceServerTrustCaBundleSecretId].filter(
        (value): value is string => Boolean(value)
      );
    }
    case CredentialKindschema.SnmpV2c: {
      const details = credential.details as CredentialDetailsSnmpV2cschema;
      return [details.readCommunitySecretId, details.writeCommunitySecretId].filter((value): value is string => Boolean(value));
    }
    case CredentialKindschema.SnmpV3: {
      const details = credential.details as CredentialDetailsSnmpV3schema;
      return [details.authPasswordSecretId, details.privPasswordSecretId].filter((value): value is string => Boolean(value));
    }
    case CredentialKindschema.ApiRest: {
      const auth = (credential.details as CredentialDetailsApiRestschema).auth;
      if (!auth) {
        return [];
      }
      const ids: Array<string | undefined> = [];
      if (auth.apiKey) {
        ids.push(auth.apiKey.apiKeySecretId);
      }
      if (auth.basic) {
        ids.push(auth.basic.passwordSecretId);
      }
      if (auth.bearer) {
        ids.push(auth.bearer.tokenSecretId);
      }
      if (auth.mtls) {
        const mtls = auth.mtls as CredentialDetailsApiRestAuthMtls;
        if ('clientCertSecretId' in mtls) {
          ids.push(mtls.clientCertSecretId, mtls.clientKeySecretId, mtls.caBundleSecretId);
        } else if ('pkcs12SecretId' in mtls) {
          ids.push(mtls.pkcs12SecretId, mtls.pkcs12PassphraseSecretId);
        }
      }
      return ids.filter((value): value is string => Boolean(value));
    }
    default:
      return [];
  }
};
